import { useCallback } from 'react';

import { debounce } from 'utils/debounce';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Callback<Args extends any[] = any[]> = (...args: Args) => void;

/**
 * Hook for use in React components.
 *
 * @param {function} fn function to execute after debounce period
 * @param {number} delay how long to wait before executing `fn`
 *
 * @returns {function} Returns function to be called with arguments to pass to `fn`
 *
 * @example
 *
 * // debounce function passed as prop
 * const Component = ({ onChange, ...props }) => {
 *    const [value, setValue] = React.useState('');
 *
 *    const debounce = useDebounce(onChange, 500);
 *
 *    const handleChange = (event) => {
 *      setValue(event.target.value);
 *      debounce(event.target.value, Date.now());
 *    }
 *
 *    return (<input onChange={handleChange} value={value} />)
 * }
 *
 * @example
 *
 * // debounce inline function (must wrap in `React.useCallback`)
 * const Component = () => {
 *    const [value, setValue] = React.useState('');
 *
 *    const debounce = useDebounce(
 *      React.useCallback(
 *        (str, timestamp) => {
 *          window.console.log({ str, timestamp });
 *        }
 *      ), 500
 *    );
 *
 *    const handleChange = (event) => {
 *      setValue(event.target.value);
 *      debounce(event.target.value, Date.now());
 *    }
 *
 *    return (<input onChange={handleChange} value={value} />)
 * }
 */
export const useDebounce = <F extends Callback>(fn: F, delay: number) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useCallback(debounce(fn, delay), [fn, delay]);
